import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import {
  DataGridPro,
  GridActionsCellItem,
  GridToolbar,
} from "@mui/x-data-grid-pro";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ArticleProperties from "../modal/ArticleProperties";

import "../styles/memoHebdo.css";
// ================= API =================
import {
  getDraftArticles,
  getPublishedArticles,
  getArchivedArticlesByYear,
  toggleArticleLock,
  DeleteArticle,
} from "../api/memoHebdo.api";

function MemoHebdo() {
  const navigate = useNavigate();
  const { t } = useTranslation(["memohebdo"]);
  const userState = useSelector((state) => state.user);
  const [articles, setArticles] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");

  const [tab, setTab] = useState("TO PUBLISHE");
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // ================= YEARS =================
  const currentYear = new Date().getFullYear();
  // ==============delete=====================
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);

  const years = [
    currentYear,
    currentYear - 1,
    currentYear - 2,
    currentYear - 3,
  ];

  const [selectedYear, setSelectedYear] = useState(currentYear);

  // ================= SNACK =================
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ================= ROLES =================
  const ADMIN_ROLES = ["ArticleAdmin", "AdminTI"];

  const canAdmin = (userState.roles || []).some((r) => ADMIN_ROLES.includes(r));

  const token = userState.token;

  // ================= LOAD ARTICLES =================
  const loadArticles = async (currentTab, year = selectedYear) => {
    try {
      setLoading(false);

      await new Promise((resolve) => setTimeout(resolve, 50));

      setLoading(true);

      let data = [];

      // DRAFT
      if (currentTab === "TO PUBLISHE") {
        data = await getDraftArticles(token);
      }

      // PUBLISHED
      if (currentTab === "PUBLISHED") {
        data = await getPublishedArticles(token);
      }

      // ARCHIVED
      if (currentTab === "ARCHIVED") {
        data = await getArchivedArticlesByYear(token, year);
      }

      await new Promise((resolve) => setTimeout(resolve, 450));

      setArticles(data || []);
    } catch (err) {
      console.error(err);

      setSnack({
        open: true,
        message: "Error loading articles",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ================= LOAD ON TAB =================
  useEffect(() => {
    if (token && tab !== "ARCHIVED") {
      loadArticles(tab);
    }
  }, [tab, token]);

  // ================= INITIAL ARCHIVE =================
  useEffect(() => {
    if (token && tab === "ARCHIVED") {
      loadArticles("ARCHIVED", selectedYear);
    }
  }, []);

  // ================= SEARCH =================
  const filteredArticles = articles.filter((a) => {
    const searchValue = search.toLowerCase();

    return (
      String(a.id).includes(searchValue) ||
      (a.titleFr || "").toLowerCase().includes(searchValue) ||
      (a.titleEn || "").toLowerCase().includes(searchValue)
    );
  });

  // ================= TITLE =================
  const getTitle = (article) => {
    return article.titleFr || article.titleEn || "No title";
  };

  // ================= LOCK =================
  const isLocked = (article) => {
    return article.idEmployeLock !== null;
  };

  // ================= STATUS COLOR =================
  const getStatusColor = () => {
    if (tab === "PUBLISHED") return "#4caf50";

    if (tab === "ARCHIVED") return "#9e9e9e";

    return "#ff9800";
  };

  // ================= DELETE =================
  // ================= CONFIRM DELETE =================
  const confirmDeleteArticle = async () => {
    if (!articleToDelete) return;

    try {
      const response = await DeleteArticle(token, articleToDelete.id);

      setSnack({
        open: true,
        message: response.message,
        severity: "success",
      });

      await loadArticles(tab, selectedYear);
    } catch (err) {
      console.error(err);

      setSnack({
        open: true,
        message: err.message || "Error deleting article",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
    }
  };
  const handleDelete = (e, article) => {
    e.stopPropagation();

    setArticleToDelete(article);

    setDeleteDialogOpen(true);
  };

  // ================= EDIT =================
  const handleEdit = (e, article) => {
    e.stopPropagation();

    navigate(`/ArticleManagement/Edit/${article.id}`);
  };
  // ================= LOCK / UNLOCK =================

  const handleToggleLock = async (e, article) => {
    e.stopPropagation();

    try {
      const response = await toggleArticleLock(token, article.id);

      if (response.success) {
        setSnack({
          open: true,
          message: response.messageFr,
          severity: "success",
        });

        // refresh local
        setArticles((prev) =>
          prev.map((a) =>
            a.id === article.id
              ? {
                  ...a,
                  idEmployeLock: a.idEmployeLock ? null : -1,
                }
              : a,
          ),
        );
      } else {
        setSnack({
          open: true,
          message: response.messageFr,
          severity: "warning",
        });
      }
    } catch (err) {
      console.error(err);

      setSnack({
        open: true,
        message: err.message || "Server error",
        severity: "error",
      });
    }
  };

  // ================= ROWS =================
  const rows = filteredArticles.map((article) => ({
    id: article.id,
    titleFr: article.titleFr,
    titleEn: article.titleEn,
    status: tab === "ARCHIVED" ? `${tab} ${selectedYear}` : tab,
    locked: article.idEmployeLock !== null,
    employeLock: article.employeLock,
    raw: article,
    publicationDate: article.publicationDate,
    endPublicationDate: article.endPublicationDate,
  }));

  //================= custon design =================
  const getStatusChipStyle = (status) => {
    switch (status) {
      case "PUBLISHED":
        return {
          backgroundColor: "#ecfdf3",
          color: "#027a48",
        };

      case "ARCHIVED":
        return {
          backgroundColor: "#f3f4f6",
          color: "#6b7280",
        };

      default:
        return {
          backgroundColor: "#fff7ed",
          color: "#ea580c",
        };
    }
  };
  // ================= COLUMNS =================
  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 110,

      renderCell: (params) => (
        <Chip
          size="small"
          label={`#${params.value}`}
          sx={{
            fontWeight: 700,
            backgroundColor: "#eff6ff",
            color: "#1976d2",
            border: "1px solid #bfdbfe",
          }}
        />
      ),
    },
    {
      field: "title",
      headerName: t("memoHebdo.grid.title"),
      flex: 1.5,
      minWidth: 400,

      renderCell: (params) => (
        <Box
          sx={{
            py: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {params.row.titleFr || "-"}
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            {params.row.titleEn || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "publicationDate",
      headerName: t("memoHebdo.grid.publicationDate"),
      width: 180,
      valueFormatter: (value) => {
        if (!value) return "";
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: "endPublicationDate",
      headerName: t("memoHebdo.grid.endPublicationDate"),
      width: 180,
      valueFormatter: (value) => {
        if (!value) return "";
        return new Date(value).toLocaleDateString();
      },
    },

    {
      field: "status",
      headerName: t("memoHebdo.grid.status"),
      width: 180,

      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.status}
          sx={{
            fontWeight: 700,
            ...getStatusChipStyle(tab),
          }}
        />
      ),
    },
    {
      field: "locked",
      headerName: "Lock",
      width: 100,
      sortable: false,
      filterable: false,

      renderCell: (params) => {
        const locked = params.row.locked;
        const lockedBy = params.row.employeLock;
        return (
          <Tooltip
            title={locked ? `Locked by ${lockedBy || "Unknown"}` : "Unlocked"}
            arrow
          >
            <IconButton
              onClick={(e) => handleToggleLock(e, params.row.raw)}
              size="small"
              sx={{
                color: locked ? "#ff9800" : "#4caf50",
                background: locked
                  ? "rgba(255,152,0,0.10)"
                  : "rgba(76,175,80,0.10)",
                border: locked
                  ? "1px solid rgba(255,152,0,0.25)"
                  : "1px solid rgba(76,175,80,0.25)",
                transition: "all 0.2s ease",

                "&:hover": {
                  background: locked
                    ? "rgba(255,152,0,0.18)"
                    : "rgba(76,175,80,0.18)",
                  transform: "scale(1.05)",
                },
              }}
            >
              {locked ? (
                <LockIcon fontSize="small" />
              ) : (
                <LockOpenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
    ...(canAdmin
      ? [
          {
            field: "actions",
            type: "actions",
            headerName: t("memoHebdo.grid.actions"),
            width: 160,

            getActions: (params) => {
              const locked = params.row.locked;

              return [
                <GridActionsCellItem
                  icon={
                    <SettingsIcon
                      sx={{
                        color: locked ? "#bdbdbd" : "#ff9800",
                      }}
                    />
                  }
                  label={t("memoHebdo.buttons.properties")}
                  disabled={locked}
                  onClick={(e) => {
                    e.stopPropagation();

                    if (locked) return;

                    setSelectedArticle(params.row.raw);
                    setPropertiesOpen(true);
                  }}
                />,

                <GridActionsCellItem
                  icon={
                    <EditIcon
                      sx={{
                        color: "#1976d2",
                      }}
                    />
                  }
                  label={t("memoHebdo.buttons.edit")}
                  onClick={(e) => handleEdit(e, params.row.raw)}
                />,

                <GridActionsCellItem
                  icon={
                    <DeleteIcon
                      sx={{
                        color: "#f44336",
                      }}
                    />
                  }
                  label={t("memoHebdo.buttons.delete")}
                  onClick={(e) => handleDelete(e, params.row.raw)}
                />,
              ];
            },
          },
        ]
      : []),
  ];

  return (
    <Box className="mh-page">
      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <Box className="mh-header-card">
        {/* TOP BAR */}
        <Box className="mh-header-topbar" />

        {/* CONTENT */}
        <Box className="mh-header-content">
          {/* TITLE */}
          <Box className="mh-header-title-row">
            <Box>
              <Typography className="mh-title">
                {t("memoHebdo.activity")}
              </Typography>

              <Typography className="mh-subtitle">
                {t("memoHebdo.activityDescription")}
              </Typography>
            </Box>

            {canAdmin && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => navigate("/ArticleManagement/Create")}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  py: 1,
                  boxShadow: "none",
                }}
              >
                {t("memoHebdo.createArticle")}
              </Button>
            )}
          </Box>

          {/* SEARCH */}
          <TextField
            size="small"
            fullWidth
            placeholder={t("memoHebdo.searchArticle")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{
                    mr: 1,
                    color: "#999",
                  }}
                />
              ),

              endAdornment: search ? (
                <IconButton onClick={() => setSearch("")}>
                  <CloseIcon />
                </IconButton>
              ) : null,
            }}
            sx={{
              mb: 3,
            }}
          />

          {/* TABS */}
          <Tabs
            value={tab}
            onChange={(e, value) => {
              setTab(value);

              if (value === "ARCHIVED") {
                loadArticles("ARCHIVED", selectedYear);
              }
            }}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                minHeight: 42,
              },
            }}
          >
            <Tab label={t("memoHebdo.tabs.draft")} value="TO PUBLISHE" />
            <Tab label={t("memoHebdo.tabs.published")} value="PUBLISHED" />
            <Tab label={t("memoHebdo.tabs.archived")} value="ARCHIVED" />
          </Tabs>

          {/* YEARS */}
          {tab === "ARCHIVED" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                mt: 2.5,
                flexWrap: "wrap",
              }}
            >
              {years.map((year) => {
                const selected = selectedYear === year;

                return (
                  <Chip
                    key={year}
                    label={
                      loading && selected ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              border: "2px solid rgba(255,255,255,0.4)",
                              borderTop: "2px solid white",
                              animation: "spin 0.8s linear infinite",
                              "@keyframes spin": {
                                "0%": {
                                  transform: "rotate(0deg)",
                                },
                                "100%": {
                                  transform: "rotate(360deg)",
                                },
                              },
                            }}
                          />

                          {year}
                        </Box>
                      ) : (
                        year
                      )
                    }
                    clickable={!loading}
                    onClick={() => {
                      setSelectedYear(year);

                      loadArticles("ARCHIVED", year);
                    }}
                    sx={{
                      px: 1,
                      height: 38,
                      fontWeight: 800,
                      fontSize: 13,
                      borderRadius: 999,
                      transition: "all 0.25s ease",
                      border: selected
                        ? "1px solid #1976d2"
                        : "1px solid #e5e7eb",
                      background: selected
                        ? "linear-gradient(135deg,#1976d2,#42a5f5)"
                        : "white",
                      color: selected ? "white" : "#374151",
                      boxShadow: selected
                        ? "0 6px 16px rgba(25,118,210,0.25)"
                        : "0 2px 8px rgba(0,0,0,0.04)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        background: selected
                          ? "linear-gradient(135deg,#1565c0,#1e88e5)"
                          : "#f9fafb",
                        boxShadow: selected
                          ? "0 8px 20px rgba(25,118,210,0.35)"
                          : "0 4px 12px rgba(0,0,0,0.08)",
                      },
                      "&:active": {
                        transform: "scale(0.97)",
                      },
                      opacity: loading && !selected ? 0.6 : 1,
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* ================================================= */}
      {/* GRID */}
      {/* ================================================= */}

      <Box
        sx={{
          position: "relative",
        }}
      >
        {/* LOADING OVERLAY */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              backdropFilter: "blur(4px)",
              background: "rgba(244,246,248,0.55)",
              borderRadius: 5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              animation: "fadeIn 0.25s ease",
              "@keyframes fadeIn": {
                from: {
                  opacity: 0,
                },
                to: {
                  opacity: 1,
                },
              },
            }}
          >
            <Box
              sx={{
                background: "white",
                px: 4,
                py: 3,
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                minWidth: 220,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  border: "4px solid rgba(25,118,210,0.15)",
                  borderTop: "4px solid #1976d2",
                  animation: "spin 0.8s linear infinite",
                  "@keyframes spin": {
                    "0%": {
                      transform: "rotate(0deg)",
                    },
                    "100%": {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              />

              <Box
                sx={{
                  textAlign: "center",
                }}
              >
                <Typography fontWeight={800} fontSize={15}>
                  {t("memoHebdo.loading.title")}
                </Typography>

                <Typography fontSize={12} color="text.secondary" mt={0.5}>
                  {t("memoHebdo.loading.description")}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Chip color="primary" label={`${filteredArticles.length} Articles`} />
        </Box>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          pagination
          paginationMode="client"
          pageSizeOptions={[20, 50, 100]}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
          }
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 20,
                page: 0,
              },
            },
          }}
          slotProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          }}
          slots={{
            toolbar: GridToolbar,
          }}
          sx={{
            border: 0,
            borderRadius: 4,
            overflow: "hidden",

            boxShadow:
              "0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e5e7eb",
              minHeight: "56px !important",
            },

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              color: "#374151",
              fontSize: 13,
            },

            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f1f5f9",
            },

            "& .MuiDataGrid-row": {
              transition: "all 0.2s ease",
            },

            "& .even-row": {
              backgroundColor: "#ffffff",
            },

            "& .odd-row": {
              backgroundColor: "#fcfcfd",
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f8fafc !important",
              cursor: "pointer",
            },

            "& .MuiDataGrid-toolbarContainer": {
              padding: "12px",
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#fafafa",
            },

            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e5e7eb",
              backgroundColor: "#fafafa",
            },
          }}
          onCellClick={(params) => {
            const field = params.field;

            if (field === "locked" || field === "actions") {
              return;
            }

            navigate(`/ArticleManagement/Details/${params.row.id}`);
          }}
        />
      </Box>

      {/* ================= DELETE ARTICLE DIALOG ================= */}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 5,
            width: 420,
            p: 3,
            textAlign: "center",
            background: "#fff",
          },
        }}
      >
        {/* ICON */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <WarningAmberRoundedIcon
            sx={{
              fontSize: 60,
              color: "#ff9800",
              background: "#fff3e0",
              borderRadius: "50%",
              p: 1.5,
            }}
          />
        </Box>

        {/* TITLE */}
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: 20,
          }}
        >
          {t("memoHebdo.articleForm.messages.deleteArticle")}
        </DialogTitle>

        {/* TEXT */}
        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: 14,
              color: "#666",
              mt: 1,
            }}
          >
            {t("memoHebdo.articleForm.messages.actionIrreversible")}{" "}
            <strong>#{articleToDelete?.id}</strong>{" "}
            {t("memoHebdo.articleForm.messages.permanentlyDeleted")}
          </DialogContentText>
        </DialogContent>

        {/* ACTIONS */}
        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              borderColor: "#ccc",
              color: "#555",

              "&:hover": {
                borderColor: "#999",
                background: "#f5f5f5",
              },
            }}
          >
            {t("memoHebdo.articleForm.buttons.cancel")}
          </Button>

          <Button
            onClick={confirmDeleteArticle}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "0 6px 16px rgba(255,0,0,0.25)",

              "&:hover": {
                boxShadow: "0 8px 20px rgba(255,0,0,0.35)",
              },
            }}
          >
            {t("memoHebdo.articleForm.buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      {/* Alert Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
      {/* Modal ArticleProperties */}
      <ArticleProperties
        open={propertiesOpen}
        onClose={() => setPropertiesOpen(false)}
        article={selectedArticle}
        onSaved={async (response) => {
          await loadArticles(tab, selectedYear);

          setSnack({
            open: true,
            severity: "success",
            message: response.message,
          });
        }}
      />
    </Box>
  );
}
export default MemoHebdo;
